import React, { useEffect, useState } from "react";
import BreadCrumb from "../../../layout/BreadCrumb";
import { Button, Collapse, Descriptions, Form, Input, Tag } from "antd";
import svg27 from "../../../assets/svg/svg27.svg";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { fetchData } from "../../../features/fetch/fetchSlice";
import { save } from "../../../features/save/saveSlice";

const { Panel } = Collapse;
const { TextArea } = Input;

function MessageTemplate() {
  const url = "api/v1/notification/fetch";
  const dispatch = useDispatch();

  const { loading, data, count } = useSelector((state) => ({
    loading: state.fetch.loading,
    data: state.fetch.data[url] || [],
    count: state.fetch.count[url] || 0,
  }));
  const transformedData = data?.flatMap((tpl) => {
    const result = [];

    if (tpl?.mstEmail) {
      result.push({
        id: `${tpl?.mstId}-email`,
        mstId: tpl?.mstId,
        name: tpl?.mstName,
        type: "email",
        content: tpl?.mstContent,
        templateHtml: tpl?.mstEmail,
        description: tpl?.mstType,
        subject: tpl?.mstSubject,
        allowedTags: tpl?.mstAllowedTags || [],
      });
    }

    if (tpl?.mstSms) {
      result.push({
        id: `${tpl?.mstId}-sms`,
        mstId: tpl?.mstId,
        name: tpl?.mstName,
        type: "sms",
        content: tpl?.mstContent,
        templateHtml: tpl?.mstSms,
        description: tpl?.mstType,
        subject: tpl?.mstSubject,
        allowedTags: tpl?.mstAllowedTags || [],
      });
    }

    return result;
  });

  const [editingId, setEditingId] = useState(null);
  const [prodd, setProdd] = useState();
  const [examplePreview, setExamplePreview] = useState(null);
  const [showTagDesc, setShowTagDesc] = useState(false);

  const [form] = Form.useForm();

  const stripHtml = (html) => {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const handleEdit = (template) => {
    setEditingId(template?.id);

    const match = template?.templateHtml.match(
      /<!-- EDITABLE_START -->([\s\S]*?)<!-- EDITABLE_END -->/
    );
    let editableContent = match ? match[1].trim() : "";

    editableContent = stripHtml(editableContent);

    form.setFieldsValue({
      ...template,
      content: editableContent,
    });

    setExamplePreview(null);
  };

  const handleSave = async (id) => {
    const updated = form.getFieldsValue();
    const [mstId, type] = id.split("-");
    const tpl = data.find((t) => t.mstId === parseInt(mstId));

    // helper: put the new content back between the markers
    const wrapWithMarkers = (content, originalHtml) => {
      return originalHtml.replace(
        /<!-- EDITABLE_START -->([\s\S]*?)<!-- EDITABLE_END -->/,
        `<!-- EDITABLE_START -->\n${content}\n<!-- EDITABLE_END -->`
      );
    };

    const updatedTpl = {
      ...tpl,
      mstEmail:
        type === "email"
          ? wrapWithMarkers(updated?.content, tpl?.mstEmail)
          : tpl?.mstEmail,
      mstSms:
        type === "sms"
          ? wrapWithMarkers(updated?.content, tpl?.mstSms)
          : tpl?.mstSms,
    };

    const res = await dispatch(
      save({
        url: "api/v1/notification/save",
        ...updatedTpl,
      })
    );

    if (res?.payload?.success) {
      toast.success(res?.payload?.messages?.message);
      setEditingId(null);
      fetchTableData();
    } else {
      toast.error(res?.payload?.messages?.message);
    }
  };

  const breadList = [
    {
      title: (
        <div className="flex items-center gap-[.25rem]">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
          >
            <g clipPath="url(#clip0_627_13942)">
              <path
                d="M13.7899 6.89067L7.75241 0.856289L7.34772 0.451602C7.25551 0.360003 7.13082 0.308594 7.00085 0.308594C6.87087 0.308594 6.74618 0.360003 6.65397 0.451602L0.211783 6.89067C0.117301 6.98478 0.0426296 7.09687 -0.00782254 7.22031C-0.0582747 7.34376 -0.0834854 7.47607 -0.0819665 7.60942C-0.0757165 8.15942 0.382096 8.59848 0.932096 8.59848H1.59616V13.6875H12.4055V8.59848H13.0837C13.3508 8.59848 13.6024 8.49379 13.7915 8.30473C13.8846 8.21193 13.9583 8.10159 14.0085 7.98009C14.0586 7.8586 14.0842 7.72836 14.0837 7.59692C14.0837 7.33129 13.979 7.07973 13.7899 6.89067ZM7.87585 12.5625H6.12585V9.37504H7.87585V12.5625ZM11.2805 7.47348V12.5625H8.87585V9.00004C8.87585 8.65473 8.59616 8.37504 8.25085 8.37504H5.75085C5.40553 8.37504 5.12585 8.65473 5.12585 9.00004V12.5625H2.72116V7.47348H1.22116L7.00241 1.69691L7.36335 2.05785L12.7821 7.47348H11.2805Z"
                fill="black"
                fillOpacity="0.45"
              />
            </g>
            <defs>
              <clipPath id="clip0_627_13942">
                <rect width="14" height="14" fill="white" />
              </clipPath>
            </defs>
          </svg>
          <span>Home</span>
        </div>
      ),
      href: "/#/dashboard",
    },
    {
      title: <div className="text-lighGold text-[16px]">Message Templates</div>,
    },
  ];

  const tagDescriptions = [
    { tag: "[FIRST_NAME]", desc: "Name of the recipient" },
    { tag: "[ORG_CODE", desc: "Organization Code" },
    { tag: "[ORG_NAME]", desc: "Name of the organization" },
    { tag: "[TOTAL BALANCE]", desc: "Total balance due" },
    { tag: "[PAYMENT INSTRUCTION]", desc: "Payment instructions to tenant" },
    {
      tag: "[SIGNATURE]",
      desc: "Signature of the company e.g By Test Company",
    },
    { tag: "[NAME]", desc: "First name of the tenant" },
    { tag: "[DATE]", desc: "Date of the reminder" },
    { tag: "[ACC NO]", desc: "Tenant Account number" },
  ];

  const generateExample = (text) =>
    (text || "")
      .replace("[FIRST_NAME]", "John")
      .replace("[ORG_CODE", "ABC217")
      .replace("[ORG_NAME]", "Smart People Africa")
      .replace("[TOTAL BALANCE]", "1,000")
      .replace(
        "[PAYMENT INSTRUCTION]",
        "To pay, use Mpesa Paybill no. 603021 and account no. 123"
      )
      .replace("[SIGNATURE]", "By Test Company")
      .replace("[NAME]", "John Doe")
      .replace("[DATE]", "25-08-2025")
      .replace("[ACC NO]", "ACC12345");

  async function fetchTableData() {
    await dispatch(
      fetchData({
        url: url,
        start: 0,
        limit: null,
      })
    );
  }

  useEffect(() => {
    fetchTableData();
  }, []);
  return (
    <div className="w-full h-full overflow-y-scroll">
      <div className="justify-between items-center white_card">
        <BreadCrumb
          breadList={breadList}
          header={"Custom Message Templates"}
          info={
            "Use this page to customize messages (SMS or Email) sent to Tenants"
          }
          btn={false}
        />
      </div>

      <div className="p-6 w-full max-w-[73rem] mx-auto mt-[2.5rem]">
        <Collapse accordion>
          {transformedData?.map((tpl) => (
            <Panel
              key={tpl?.id}
              header={
                <div className="grid grid-cols-[1fr_3fr_auto] gap-4 text-[#8493A5] font-dmSans font-[600] text-[16px]">
                  <div className="flex items-center gap-2">
                    <span>{tpl?.name}</span>
                    <Tag
                      className="rounded-[1rem]"
                      color={tpl?.type === "email" ? "blue" : "green"}
                    >
                      {tpl?.type}
                    </Tag>
                  </div>

                  <div
                    className="line-clamp-1 text-ellipsis overflow-hidden"
                    style={{
                      display: "-webkit-box",
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {tpl?.content}
                  </div>

                  <div className="text-end">
                    <button onClick={() => setProdd(tpl)}>
                      <img src={svg27} alt="svg27" />
                    </button>
                  </div>
                </div>
              }
            >
              {editingId === tpl?.id ? (
                <Form form={form} layout="vertical" className="">
                  <Form.Item name="name" label="Name">
                    <Input className="input" disabled />
                  </Form.Item>

                  <Form.Item name="description" label="Description">
                    <Input className="input" disabled />
                  </Form.Item>

                  <Form.Item name="type" label="Type">
                    <Input className="input" disabled />
                  </Form.Item>

                  <div className="mb-4">
                    <div className="flex flex-col flex-wrap items-start gap-3">
                      <div>
                        <div className="font-semibold text-[16px]">
                          Template
                        </div>
                        {/* <p className="text-[16px] text-[#8493A5] ">
                          Add Tags to pass dynamic values. Allowed tags are
                          <br />
                          {tpl?.allowedTags?.map((t, i) => (
                            <span key={t}>
                              <b>{t}</b>
                              {i < tpl?.allowedTags?.length - 1 ? ", " : ""}
                            </span>
                          ))}
                        </p> */}
                      </div>

                      <Button
                        onClick={() =>
                          setShowTagDesc((prev) =>
                            prev === tpl?.id ? null : tpl?.id
                          )
                        }
                        size="small"
                        className="!rounded-md !bg-[#E4E4FF] !text-[#111827]"
                      >
                        {showTagDesc === tpl?.id
                          ? "Hide tag description"
                          : "View tag description"}
                      </Button>
                    </div>

                    {showTagDesc === tpl?.id && (
                      <div className="mt-3 border rounded-md p-4 text-[16px] text-[#8493A5]">
                        {tagDescriptions
                          ?.filter((item) =>
                            tpl?.allowedTags?.includes(item?.tag)
                          )
                          ?.map((item) => (
                            <p key={item?.tag} className="mb-2">
                              <b>{item?.tag}</b> : {item?.desc}
                            </p>
                          ))}
                      </div>
                    )}
                  </div>

                  <Form.Item name="content" label="Content">
                    <TextArea rows={7} className="input-textarea" />
                  </Form.Item>

                  <Button
                    onClick={() => {
                      const content = form.getFieldValue("content");
                      if (examplePreview) {
                        setExamplePreview(null);
                      } else {
                        setExamplePreview(generateExample(content));
                      }
                    }}
                    size="small"
                    className="!rounded-md !bg-[#E4E4FF] !text-[#111827]"
                  >
                    {examplePreview ? "Hide example" : "See example"}
                  </Button>

                  {examplePreview && (
                    <div className="mt-4 p-3 border rounded-md bg-[#f4f4f4]">
                      <h4 className="font-medium mb-1">Example Preview</h4>
                      <p>{examplePreview}</p>
                    </div>
                  )}

                  <div className="flex flex-col gap-4 mt-4">
                    <button
                      type="button"
                      className="cstm-btn-2 !rounded-[.5rem] "
                      onClick={() => handleSave(tpl?.id)}
                    >
                      Update Template
                    </button>
                    <button
                      type="button"
                      className="cstm-btn !rounded-[.5rem] "
                      onClick={() => setEditingId(null)}
                    >
                      Clear
                    </button>
                  </div>
                </Form>
              ) : (
                <div>
                  <Descriptions
                    className="template-desc template-desc-label"
                    column={1}
                    colon={false}
                    items={[
                      {
                        key: "1",
                        label: "Name",
                        children: tpl?.name,
                      },
                      {
                        key: "2",
                        label: "Type",
                        children: (
                          <Tag color={tpl?.type === "email" ? "blue" : "green"}>
                            {tpl?.type}
                          </Tag>
                        ),
                      },
                      {
                        key: "3",
                        label: "Description",
                        children: tpl?.description || "No description",
                      },
                      {
                        key: "4",
                        label: "Content",
                        children: tpl?.content,
                      },
                    ]}
                  />
                  <button
                    type="button"
                    className="mt-3 !p-2 cstm-btn-2 !w-[230px] !rounded-[0px]"
                    onClick={() => handleEdit(tpl)}
                  >
                    Edit Custom Template
                  </button>
                </div>
              )}
            </Panel>
          ))}
        </Collapse>
      </div>
    </div>
  );
}

export default MessageTemplate;
